import React, { useEffect, useState } from "react";
import { IMG_BASE_URL } from "../../utils/utils";
import { Box, Typography, useMediaQuery, IconButton } from "@mui/material";
import { useTheme } from "@emotion/react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

const SimpleCarousel = ({
  slides = [],
  tags,
  style,
  handleChildData,
  info,
  infoClr,
  bgClr
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const convertToArray = (input) =>
    input
      ?.slice(1, -1)
      ?.split("][")
      ?.map((item) => item.trim());

  const imageArray = convertToArray(slides) || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
  const thumbnailsPerPage = isSmallScreen ? 3 : 5;
  const [imgErrors, setImgErrors] = useState({});
  const [mainImgError, setMainImgError] = useState(false);

  const handleThumbnailHover = (index) => {
    setCurrentIndex(index);
  };

  const handleNextThumbnails = () => {
    if (thumbnailStartIndex + thumbnailsPerPage < imageArray.length) {
      setThumbnailStartIndex((prev) =>
        Math.min(
          prev + thumbnailsPerPage,
          imageArray.length - thumbnailsPerPage
        )
      );
    }
  };

  const handlePrevThumbnails = () => {
    if (thumbnailStartIndex > 0) {
      setThumbnailStartIndex((prev) => Math.max(prev - thumbnailsPerPage, 0));
    }
  };

  const visibleThumbnails = imageArray.slice(
    thumbnailStartIndex,
    thumbnailStartIndex + thumbnailsPerPage
  );
  useEffect(() => {
    handleChildData(mainImgError);
  }, [mainImgError]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: bgClr ?? "#F9F8F6",
        p: 1,
        // bgcolor: "#FDFAF6",
        borderRadius: "16px",

        mb: 2,
      }}
    >
      {/* Main Image */}
      {!mainImgError && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "600px",
            aspectRatio: "16 / 9",
            borderRadius: "16px",
            overflow: "hidden",
            mb: 2,
          }}
        >
          {imageArray[currentIndex] && (
            <img
              src={`${IMG_BASE_URL}/upload/WebshopProductsAdditionalImages${imageArray[
                currentIndex
              ]?.replace(/\\/g, "/")}`}
              alt={`Slide ${currentIndex}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                //backgroundColor: "#000",
                transition: "opacity 0.5s ease-in-out",
              }}
              onError={() => setMainImgError(true)}
            />
          )}

          {tags?.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 16,
                left: 16,
                display: "flex",
                gap: 1,
                zIndex: 2,
                flexWrap: "wrap",
              }}
            >
              {tags.map((tag, index) => (
                <Typography
                  key={index}
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    px: 1,
                    py: "2px",
                    borderRadius: "4px",
                    backgroundColor: tag.bgColor,
                    color: tag.textColor,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  {tag.text}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Thumbnail Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: "600px",
          gap: 1,
          mt: 1,
        }}
      >
        {/* Previous Button */}
        {thumbnailStartIndex > 0 && (
          <IconButton
            onClick={handlePrevThumbnails}
            sx={{
              backgroundColor: "#f1f1f1",
              "&:hover": { backgroundColor: "#e0e0e0" },
              borderRadius: "50%",
              p: 1,
            }}
          >
            <ArrowBackIosIcon fontSize="small" />
          </IconButton>
        )}

        {/* Thumbnails */}
        {visibleThumbnails.map((image, index) => {
          const actualIndex = thumbnailStartIndex + index;
          if (imgErrors[actualIndex]) return null;
          return (
            <Box
              key={actualIndex}
              onMouseEnter={() => handleThumbnailHover(actualIndex)}
              sx={{
                width: isSmallScreen ? 60 : 80,
                height: isSmallScreen ? 60 : 80,
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                border:
                  currentIndex === actualIndex
                    ? "2px solid #F37120"
                    : "2px solid transparent",
                transition: "transform 0.2s",
                "&:hover": { transform: "scale(1.05)" },
              }}
            >
              <img
                src={`${IMG_BASE_URL}/upload/WebshopProductsAdditionalImages${image.replace(
                  /\\/g,
                  "/"
                )}`}
                alt={`Thumbnail ${actualIndex}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => {
                  setImgErrors((prev) => ({ ...prev, [actualIndex]: true }));
                }}
              />
            </Box>
          );
        })}

        {/* Next Button */}
        {thumbnailStartIndex + thumbnailsPerPage < imageArray.length && (
          <IconButton
            onClick={handleNextThumbnails}
            sx={{
              backgroundColor: "#f1f1f1",
              "&:hover": { backgroundColor: "#e0e0e0" },
              borderRadius: "50%",
              p: 1,
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {info && (
        <Typography
          variant="body2"
          sx={{
            color: infoClr,
            mt: 2,
            fontSize: "14px",
            whiteSpace: "pre-line",
            mb: 3,
          }}
        >
          {info}
        </Typography>
      )}
    </Box>
  );
};

export default SimpleCarousel;
